import { Link } from 'react-router-dom';
import { Collaboration } from '../../types';
import { formatRelativeTime } from '../../utils/dateUtils';
import { endpointDisplayName } from '../../utils/endpointLabel';

interface CollaborationCardProps {
  collaboration: Collaboration;
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '-';
  if (!completedAt) return 'Running...';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return '< 1s';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

function getStatusBadgeClass(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
    case 'running':
      return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
    case 'failed':
      return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
    case 'skipped':
      return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
    case 'waiting':
    case 'waiting_for_input':
      return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
    case 'pending':
    default:
      return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
  }
}

function getMethodBadgeClass(method: string) {
  switch (method) {
    case 'GET':
      return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
    case 'POST':
      return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
    case 'PUT':
      return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
    case 'PATCH':
      return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
    case 'DELETE':
      return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  }
}

export default function CollaborationCard({ collaboration }: CollaborationCardProps) {
  const endpoint = collaboration.endpointRequest?.endpoint;
  const duration = formatDuration(collaboration.startedAt, collaboration.completedAt);

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
      <Link to={`/collaborations/${collaboration.id}`} className="block">
        {/* Primary: Status badge and endpoint */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(collaboration.status)}`}>
                {collaboration.status}
              </span>
              {endpoint && (
                <div className="flex items-center gap-1">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getMethodBadgeClass(endpoint.method)}`}>
                    {endpoint.method}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                    {endpointDisplayName(endpoint)}
                  </span>
                </div>
              )}
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 truncate">
              {collaboration.id.slice(0, 8)}...
            </h3>
          </div>
        </div>

        {/* Secondary: Duration and config */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          {duration !== '-' && (
            <span>{duration}</span>
          )}
          {collaboration.collaborationConfig && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
              {collaboration.collaborationConfig.name}
            </span>
          )}
        </div>

        {/* Metadata: Updated time */}
        <div className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Updated {formatRelativeTime(collaboration.updatedAt)}
        </div>
      </Link>

    </div>
  );
}
